// ============================================================================
// API — Configuración del editor de tienda (panel-cliente).
//
// La config del constructor visual se persiste en `configuracion_empresa`
// con clave `editor_tienda` (additiva: NO toca `tema_visual`, que ya consume
// el storefront vía publicar_tienda). Agrupa aquello que el editor aporta
// por encima del tema de colores: variante de header, estilo de botones,
// estilo de tarjetas y orden de secciones.
//
// El editor combina:
//   - `tema_visual`   (colores, tipografía, sombras, espaciado, radio) — API tema.js
//   - `editor_tienda` (esto) — estructura del editor visual.
// ============================================================================

import { supabaseClient } from '@plataforma/supabase-client';

import { obtenerEstadoSesion } from '../estado-sesion.js';

import { marcarCambiosPendientes } from './estado-publicacion.js';

export const CLAVE_EDITOR_TIENDA = 'editor_tienda';

export const EDITOR_TIENDA_POR_DEFECTO = {
  header: 'clasico',
  categorias_variante: 'clasico',
  botones: {
    estilo: 'redondeado',
    radio: 8,
    ancho_completo: false,
  },
  tarjetas: {
    estilo: 'borde',
    radio: 10,
    sombra: 'suave',
  },
  seccion_orden: [
    'banner_principal',
    'categorias',
    'propuesta_valor',
    'productos_destacados',
    'testimonios',
    'footer',
  ],
  tema_preset: 'moderno',
  // ---- Presentación de las secciones de la portada -----------------------
  // El CONTENIDO (banners, puntos de confianza, productos, testimonios,
  // contacto, footer) se administra en sus módulos; aquí solo vive la
  // PRESENTACIÓN (variante y textos de cabecera), para respetar "no dupliques
  // datos": Personalizar tienda decide CÓMO se ve, los módulos QUÉ se muestra.
  banner: {
    variante: 'hero',
    overlay: 54,
    alineacion: 'left',
    autoplay: true,
    etiqueta: '',
    texto_boton: 'Ver catálogo',
  },
  propuesta_valor: { variante: 'banda', titulo: '' },
  productos: { variante: 'clasico', titulo: '', eyebrow: '', mostrar_ver_todo: true },
  testimonios: { variante: 'tarjetas', titulo: 'Testimonios' },
  contacto: { variante: 'resumen', titulo: '¿Tienes una duda antes de comprar?', texto: '' },
  footer: { variante: 'clasico' },
};

const HEADERS_VALIDOS = new Set([
  'clasico',
  'ecommerce',
  'premium',
  'minimal',
  'moderno',
  'marketplace',
  'editorial',
  'flotante',
]);
export { HEADERS_VALIDOS };
const VARIANTES_CATEGORIAS_VALIDAS = new Set([
  'clasico',
  'editorial',
  'mosaico',
  'panorama',
  'compacto',
  'horizontal',
  'magazine',
  'minimal',
]);
export { VARIANTES_CATEGORIAS_VALIDAS };
const ESTILOS_BOTONES_VALIDOS = new Set(['redondeado', 'cuadrado', 'pill', 'outline']);
const ESTILOS_TARJETAS_VALIDOS = new Set(['borde', 'relleno', 'elevado']);
const SOMBRAS_TARJETAS_VALIDAS = new Set(['ninguna', 'suave', 'media']);

// Variantes de presentación de cada sección de la portada. Estos SÓLO afectan
// a cómo se componen visualmente las secciones en el storefront (espejo de
// motortema.js). El contenido real vive en los módulos, no aquí.
export const VARIANTES_PROPUESTA_VALIDAS = new Set(['banda', 'tarjetas', 'editorial', 'minimal']);
export const VARIANTES_PRODUCTOS_VALIDAS = new Set(['clasico', 'lista', 'compacto', 'destacado']);
export const VARIANTES_TESTIMONIOS_VALIDAS = new Set([
  'tarjetas',
  'central',
  'minimal',
  'carrusel',
]);
export const VARIANTES_CONTACTO_VALIDAS = new Set(['resumen', 'centrado', 'tarjeta']);
export const VARIANTES_FOOTER_VALIDAS = new Set(['clasico', 'centrado', 'minimal']);
const BANNER_ALINEACIONES_VALIDAS = new Set(['left', 'center', 'right']);

function empresaIdActual() {
  return obtenerEstadoSesion().claims.empresa_id;
}

export function normalizarEditorTienda(config) {
  const base = EDITOR_TIENDA_POR_DEFECTO;
  const entrada = config ?? {};

  const botones = {
    ...base.botones,
    ...(entrada.botones ?? {}),
  };
  if (!ESTILOS_BOTONES_VALIDOS.has(botones.estilo)) botones.estilo = base.botones.estilo;
  botones.radio = clamp(Number(botones.radio) || base.botones.radio, 0, 32);
  botones.ancho_completo = Boolean(botones.ancho_completo);

  const tarjetas = {
    ...base.tarjetas,
    ...(entrada.tarjetas ?? {}),
  };
  if (!ESTILOS_TARJETAS_VALIDOS.has(tarjetas.estilo)) tarjetas.estilo = base.tarjetas.estilo;
  tarjetas.radio = clamp(Number(tarjetas.radio) || base.tarjetas.radio, 0, 32);
  if (!SOMBRAS_TARJETAS_VALIDAS.has(tarjetas.sombra)) tarjetas.sombra = base.tarjetas.sombra;

  const banner = { ...base.banner, ...(entrada.banner ?? {}) };
  if (!BANNER_ALINEACIONES_VALIDAS.has(banner.alineacion))
    banner.alineacion = base.banner.alineacion;
  if (banner.overlay === undefined || banner.overlay === null) banner.overlay = base.banner.overlay;
  banner.overlay = clamp(Number(banner.overlay) || base.banner.overlay, 10, 78);
  banner.autoplay = banner.autoplay !== false;
  banner.texto_boton = String(banner.texto_boton ?? '').trim() || base.banner.texto_boton;
  banner.etiqueta = String(banner.etiqueta ?? '');

  const propuesta_valor = {
    ...base.propuesta_valor,
    ...(entrada.propuesta_valor ?? {}),
  };
  propuesta_valor.variante = VARIANTES_PROPUESTA_VALIDAS.has(propuesta_valor.variante)
    ? propuesta_valor.variante
    : base.propuesta_valor.variante;
  propuesta_valor.titulo = String(propuesta_valor.titulo ?? '');

  const productos = { ...base.productos, ...(entrada.productos ?? {}) };
  productos.variante = VARIANTES_PRODUCTOS_VALIDAS.has(productos.variante)
    ? productos.variante
    : base.productos.variante;
  productos.titulo = String(productos.titulo ?? '');
  productos.eyebrow = String(productos.eyebrow ?? '');
  productos.mostrar_ver_todo = productos.mostrar_ver_todo !== false;

  const testimonios = { ...base.testimonios, ...(entrada.testimonios ?? {}) };
  testimonios.variante = VARIANTES_TESTIMONIOS_VALIDAS.has(testimonios.variante)
    ? testimonios.variante
    : base.testimonios.variante;
  testimonios.titulo = String(testimonios.titulo ?? '');

  const contacto = { ...base.contacto, ...(entrada.contacto ?? {}) };
  contacto.variante = VARIANTES_CONTACTO_VALIDAS.has(contacto.variante)
    ? contacto.variante
    : base.contacto.variante;
  contacto.titulo = String(contacto.titulo ?? '');
  contacto.texto = String(contacto.texto ?? '');

  const footer = { ...base.footer, ...(entrada.footer ?? {}) };
  footer.variante = VARIANTES_FOOTER_VALIDAS.has(footer.variante)
    ? footer.variante
    : base.footer.variante;

  return {
    header: HEADERS_VALIDOS.has(entrada.header) ? entrada.header : base.header,
    categorias_variante: VARIANTES_CATEGORIAS_VALIDAS.has(entrada.categorias_variante)
      ? entrada.categorias_variante
      : base.categorias_variante,
    botones,
    tarjetas,
    seccion_orden: validarSeccionOrden(entrada.seccion_orden, base.seccion_orden),
    tema_preset: typeof entrada.tema_preset === 'string' ? entrada.tema_preset : base.tema_preset,
    banner,
    propuesta_valor,
    productos,
    testimonios,
    contacto,
    footer,
  };
}

function clamp(valor, min, max) {
  return Math.min(max, Math.max(min, valor));
}

// Mantiene solo códigos de sección conocidos del home y en orden único.
const SECCIONES_CONOCIDAS = new Set([
  'banner_principal',
  'categorias',
  'propuesta_valor',
  'productos_destacados',
  'productos_nuevos',
  'promociones',
  'novedades',
  'testimonios',
  'footer',
]);

function validarSeccionOrden(orden, porDefecto) {
  if (!Array.isArray(orden) || orden.length === 0) return [...porDefecto];
  const unicos = [...new Set(orden)].filter((codigo) => SECCIONES_CONOCIDAS.has(codigo));
  if (unicos.length === 0) return [...porDefecto];
  // Conservar el orden elegido e incorporar al final las secciones conocidas
  // que no fueron incluidas, para que el listado nunca quede incompleto.
  const incluidas = new Set(unicos);
  for (const codigo of porDefecto) {
    if (!incluidas.has(codigo)) unicos.push(codigo);
  }
  return unicos;
}

export async function obtenerEditorTienda() {
  const { data, error } = await supabaseClient()
    .from('configuracion_empresa')
    .select('valor')
    .eq('empresa_id', empresaIdActual())
    .eq('clave', CLAVE_EDITOR_TIENDA)
    .maybeSingle();

  if (error) return { data: EDITOR_TIENDA_POR_DEFECTO, error };

  return { data: normalizarEditorTienda(data?.valor), error: null };
}

export async function guardarEditorTienda(config) {
  const normalizado = normalizarEditorTienda(config);

  const resultado = await supabaseClient()
    .from('configuracion_empresa')
    .upsert(
      {
        empresa_id: empresaIdActual(),
        clave: CLAVE_EDITOR_TIENDA,
        valor: normalizado,
      },
      { onConflict: 'empresa_id,clave' },
    )
    .select()
    .single();

  // Igual que tema_visual: configuracion_empresa no tiene `actualizado_en`,
  // así que el "hay cambios pendientes de publicar" se enciende localmente.
  if (!resultado.error) marcarCambiosPendientes();

  return resultado;
}
